import {createEisTypeObject, getKey} from 'Quoting/utils/utilities';
import {Map as ImmutableMap} from 'immutable';

export const createNewGCI5PlanObjectWithDefault = (
  plan: any,
  rootId: string,
  quoteParentId: string,
  coverageEffectiveDate: string,
  defaultValues: ImmutableMap<string, any>,
  situsStateCd: string
) => {
  const key = getKey(rootId, quoteParentId);
  const parentId = key.id;

  return {
    exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId),
    planCd: plan.planCode,
    planGroupsLinks: [],
    _type: 'CIBenefitPlan',
    planName: plan.name,
    _key: key,
    coverageDefs: plan.coverages
      .filter((coverage: any) => coverage.applicability === 'Mandatory')
      .map(() => createCoverageDef(defaultValues, rootId, parentId, coverageEffectiveDate, situsStateCd)),
    showRateAdjustmentWarning: defaultValues.get('CIBenefitPlan.showRateAdjustmentWarning'),
    offerStatus: 'offered',
  };
};

const createCoverageDef = (
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  planParentId: string,
  coverageEffectiveDate: string,
  situsStateCd: string
) => {
  const key = getKey(planRootId, planParentId);
  const rootId = planRootId;
  const parentId = key.id;
  return {
    _key: key,
    code: defaultValues.get('CICoverageDefinition.code'),
    preExLimitation: {
      preExConditionLimitationCd: defaultValues.get('CIPreExLimitation.preExConditionLimitationCd'),
      ...createEisTypeObject('CIPreExLimitation', rootId, parentId),
    },
    isPreExistingConditionsApplied: defaultValues.get('CICoverageDefinition.isPreExistingConditionsApplied'),
    terminationAgeCd: defaultValues.get('CICoverageDefinition.terminationAgeCd'),
    planStructure: defaultValues.get('CICoverageDefinition.planStructure'),
    benefitStructures: createCIBenefitStructure(defaultValues, rootId, parentId),
    effDate: coverageEffectiveDate,
    eligibilities: createCIEligibilities(defaultValues, rootId, parentId),
    whoCovered: createWhoCovered(rootId, parentId),
    ratingDetails: {
      rateFormatCd: defaultValues.get('CICoverageRating.rateFormatCd'),
      ...createEisTypeObject('CICoverageRating', rootId, parentId),
    },
    originalEffDate: coverageEffectiveDate,
    // censusTypeCd: defaultValues.get('CICoverageDefinition.censusTypeCd'),
    coverageGroupsLinks: [],
    _type: 'CICoverageDefinition',
    fundingStructure: {
      ...createEisTypeObject('CIFundingStructure', rootId, parentId),
      requiredGroupAccessPctCd: defaultValues.get('CIFundingStructure.requiredGroupAccessPctCd'),
      taxabilityCd: defaultValues.get('CIFundingStructure.taxabilityCd'),
      minParticipantsNum: defaultValues.get('CIFundingStructure.minParticipantsNum'),
      contributionTypeCd: defaultValues.get('CIFundingStructure.contributionTypeCd'),
      fundingTypeCd: defaultValues.get('CIFundingStructure.fundingTypeCd'),
      requiredParticipationPct: defaultValues.get('CIFundingStructure.requiredParticipationPct'),
      payrollFrequency: undefined,
    },
    benefitSchedule: createBenefitSchedule(defaultValues, rootId, parentId, situsStateCd),
    offerStatus: 'offered',
    issueAge: {
      minAgeIndividual: defaultValues.get('CIEligibilityIssueAges.minAgeIndividual'),
      minAgeChildren: defaultValues.get('CIEligibilityIssueAges.minAgeChildren'),
      maxAgeChildren: defaultValues.get('CIEligibilityIssueAges.maxAgeChildren'),
      minAgeSpouse: defaultValues.get('CIEligibilityIssueAges.minAgeSpouse'),
      ...createEisTypeObject('CIEligibilityIssueAges', rootId, parentId),
    },
    underwritingDetails: {
      underwritingTypeCd: defaultValues.get('CIUnderwriting.underwritingTypeCd'),
      isEOIRequired: defaultValues.get('CIUnderwriting.isEOIRequired'),
      indGIAmount: defaultValues.get('CIUnderwriting.indGIAmount'),
      ...createEisTypeObject('CIUnderwriting', rootId, parentId),
    },
    whoCoveredCd: defaultValues.get('CICoverageDefinition.whoCoveredCd'),
  };
};

const createBenefitPlanExclusions = (defaultValues: ImmutableMap<string, any>, rootId?: string, parentId?: string) =>
  createExclusions(
    [
      {
        exclusionEntityName: 'CIExclIntentionallySelfInflictedInjury',
        isExclusion: defaultValues.get('CIExclIntentionallySelfInflictedInjury.isExclusion') ?? true,
      },
      {
        exclusionEntityName: 'CIExclAlcoholAbuse',
        isExclusion: defaultValues.get('CIExclAlcoholAbuse.isExclusion') ?? true,
      },
      {
        exclusionEntityName: 'CIExclDrugAbuse',
        isExclusion: defaultValues.get('CIExclDrugAbuse.isExclusion') ?? true,
      },
      {
        exclusionEntityName: 'CIExclDiagnosedBeforeEffectiveDate',
        isExclusion: defaultValues.get('CIExclDiagnosedBeforeEffectiveDate.isExclusion') ?? false,
      },
      {
        exclusionEntityName: 'CIExclDiagnosedOutsideOfTheUS',
        isExclusion: defaultValues.get('CIExclDiagnosedOutsideOfTheUS.isExclusion') ?? true,
      },
    ],
    rootId,
    parentId
  );

const createExclusions = (
  exclusions: {exclusionEntityName: string; isExclusion: boolean}[],
  rootId?: string,
  parentId?: string
) =>
  exclusions.map(({exclusionEntityName, isExclusion}) => ({
    ...createEisTypeObject(exclusionEntityName, rootId, parentId),
    isExclusion: isExclusion,
  }));

const createCIEligibilities = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return [
    {
      waitingPeriodAmount: defaultValues.get('CIEligibility.waitingPeriodAmount'),
      waitingPeriodModeCd: defaultValues.get('CIEligibility.waitingPeriodModeCd'),
      waitingPeriodDefCd: defaultValues.get('CIEligibility.waitingPeriodDefCd'),
      minHourlyReq: defaultValues.get('CIEligibility.minHourlyReq'),
      eligibilityTypeCd: 'ExistingEmployees',
    },
    {
      waitingPeriodModeCd: defaultValues.get('CIEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('CIEligibility.minHourlyReq'),
      eligibilityTypeCd: 'NewEmployees',
    },
  ].map(e => ({...e, ...createEisTypeObject('CIEligibility', rootId, parentId)}));
};

const createBenefitSchedule = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  situsStateCd: string
) => {
  return {
    isSeparationPeriodApplied: defaultValues.get('CIBenefitSchedule.isSeparationPeriodApplied'),
    separationPeriodDetails: {
      separationPeriodCd: defaultValues.get('CISeparationPeriodBenefit.separationPeriodCd'),
      ...createEisTypeObject('CISeparationPeriodBenefit', rootId, parentId),
    },
    waiverOfPremiumDetails: {
      waitingPeriod: defaultValues.get('CIWaiverOfPremiumBenefit.waitingPeriod'),
      benefitDuration: defaultValues.get('CIWaiverOfPremiumBenefit.benefitDuration'),
      waiverTypeCd: defaultValues.get('CIWaiverOfPremiumBenefit.waiverTypeCd'),
      ...createEisTypeObject('CIWaiverOfPremiumBenefit', rootId, parentId),
    },
    isSpecConditionAndInfDiseaseApplied: defaultValues.get('CIBenefitSchedule.isSpecConditionAndInfDiseaseApplied'),
    lifetimeMaximumDetails: {
      maxBenefitAmountPct: defaultValues.get('CILifetimeMaximumBenefit.maxBenefitAmountPct'),
      ...createEisTypeObject('CILifetimeMaximumBenefit', rootId, parentId),
    },
    isMentalNervousOrDisorderAndSubstanceAbuseRiderApplied: defaultValues.get(
      'CIBenefitSchedule.isMentalNervousOrDisorderAndSubstanceAbuseRiderApplied'
    ),
    childhoodConditionsDetails: {
      childhoodConditionsPct: defaultValues.get('CIChildhoodConditions.childhoodConditionsPct'),
      neonatalICUPct: defaultValues.get('CIChildhoodConditions.neonatalICUPct'),
      isCerebralPalsy: defaultValues.get('CIChildhoodConditions.isCerebralPalsy'),
      isCleftLipOrPalate: defaultValues.get('CIChildhoodConditions.isCleftLipOrPalate'),
      isCongenitalHeartDisease: defaultValues.get('CIChildhoodConditions.isCongenitalHeartDisease'),
      isCysticFibrosis: defaultValues.get('CIChildhoodConditions.isCysticFibrosis'),
      isDownSyndrome: defaultValues.get('CIChildhoodConditions.isDownSyndrome'),
      isMuscularDystrophy: defaultValues.get('CIChildhoodConditions.isMuscularDystrophy'),
      isSpinaBifida: defaultValues.get('CIChildhoodConditions.isSpinaBifida'),
      isStructuralCongenitalDefect: defaultValues.get('CIChildhoodConditions.isStructuralCongenitalDefect'),
      isTypeOneDiabetes: defaultValues.get('CIChildhoodConditions.isTypeOneDiabetes'),
      ...createEisTypeObject('CIChildhoodConditions', rootId, parentId),
    },
    cancerConditionsDetails: {
      conditionsDetails: [
        {
          carcinomaSituPct: defaultValues.get('CICancerConditionsInitialOrRecurrence.carcinomaSituPct'),
          invasiveCancerPct: defaultValues.get('CICancerConditionsInitialOrRecurrence.invasiveCancerPct'),
          cancerConditionsTypeCd: 'InitialDiagnosis',
        },
        {
          cancerConditionsTypeCd: 'Recurrence',
          carcinomaSituPct: defaultValues.get('CICancerConditionsInitialOrRecurrence.carcinomaSituPct'),
          invasiveCancerPct: defaultValues.get('CICancerConditionsInitialOrRecurrence.invasiveCancerPct'),
        },
      ].map(e => ({...e, ...createEisTypeObject('CICancerConditionsInitialOrRecurrence', rootId, parentId)})),
      ...createEisTypeObject('CICancerConditions', rootId, parentId),
    },
    coveredConditionsDetails: [
      {
        coveredConditionsTypeCd: 'InitialDiagnosis',
        occupationalHIVPct: defaultValues.get('CICoveredConditions.occupationalHIVPct'),
        lossOfHearingPct: defaultValues.get('CICoveredConditions.lossOfHearingPct'),
        lossOfSightPct: defaultValues.get('CICoveredConditions.lossOfSightPct'),
        arrhythmiaICDPlacementPct: defaultValues.get('CICoveredConditions.arrhythmiaICDPlacementPct'),
        boneMarrowOrStemCellTransplantPct: defaultValues.get('CICoveredConditions.boneMarrowOrStemCellTransplantPct'),
        kidneyRenalFailurePct: defaultValues.get('CICoveredConditions.kidneyRenalFailurePct'),
        lossOfSpeechPct: defaultValues.get('CICoveredConditions.lossOfSpeechPct'),
        majorOrganFailurePct: defaultValues.get('CICoveredConditions.majorOrganFailurePct'),
        crohnDiseasePct: defaultValues.get('CICoveredConditions.crohnDiseasePct'),
        strokePct: defaultValues.get('CICoveredConditions.strokePct'),
        advancedParkinsonsPct: defaultValues.get('CICoveredConditions.advancedParkinsonsPct'),
        coronaryArteryDiseasePct: defaultValues.get('CICoveredConditions.coronaryArteryDiseasePct'),
        suddenCardiacArrestPct: defaultValues.get('CICoveredConditions.suddenCardiacArrestPct'),
        tiaOrRindPct: defaultValues.get('CICoveredConditions.tiaOrRindPct'),
        benignBrainTumorPct: defaultValues.get('CICoveredConditions.benignBrainTumorPct'),
        comaPct: defaultValues.get('CICoveredConditions.comaPct'),
        coronaryArteryDiseaseAngioplastyPct: defaultValues.get(
          'CICoveredConditions.coronaryArteryDiseaseAngioplastyPct'
        ),
        arrhythmiaPacemakerPlacementPct: defaultValues.get('CICoveredConditions.arrhythmiaPacemakerPlacementPct'),
        boneMarrowOrStemCellDonationPct: defaultValues.get('CICoveredConditions.boneMarrowOrStemCellDonationPct'),
        pulmonaryEmbolismPct: defaultValues.get('CICoveredConditions.pulmonaryEmbolismPct'),
        aorticOrMitralValveRepairOrReplacementPct: defaultValues.get(
          'CICoveredConditions.aorticOrMitralValveRepairOrReplacementPct'
        ),
        heartAttackPct: defaultValues.get('CICoveredConditions.heartAttackPct'),
        idiopathicPulmonaryFibrosisPct: defaultValues.get('CICoveredConditions.idiopathicPulmonaryFibrosisPct'),
        paralysisPct: defaultValues.get('CICoveredConditions.paralysisPct'),
        advancedAlzheimersPct: defaultValues.get('CICoveredConditions.advancedAlzheimersPct'),
      },
      {
        coveredConditionsTypeCd: 'Recurrence',
        occupationalHIVPct: defaultValues.get('CICoveredConditions.occupationalHIVPct'),
        lossOfHearingPct: defaultValues.get('CICoveredConditions.lossOfHearingPct'),
        lossOfSightPct: defaultValues.get('CICoveredConditions.lossOfSightPct'),
        arrhythmiaICDPlacementPct: defaultValues.get('CICoveredConditions.arrhythmiaICDPlacementPct'),
        boneMarrowOrStemCellTransplantPct: defaultValues.get('CICoveredConditions.boneMarrowOrStemCellTransplantPct'),
        kidneyRenalFailurePct: defaultValues.get('CICoveredConditions.kidneyRenalFailurePct'),
        lossOfSpeechPct: defaultValues.get('CICoveredConditions.lossOfSpeechPct'),
        majorOrganFailurePct: defaultValues.get('CICoveredConditions.majorOrganFailurePct'),
        crohnDiseasePct: defaultValues.get('CICoveredConditions.crohnDiseasePct'),
        strokePct: defaultValues.get('CICoveredConditions.strokePct'),
        advancedParkinsonsPct: defaultValues.get('CICoveredConditions.advancedParkinsonsPct'),
        coronaryArteryDiseasePct: defaultValues.get('CICoveredConditions.coronaryArteryDiseasePct'),
        suddenCardiacArrestPct: defaultValues.get('CICoveredConditions.suddenCardiacArrestPct'),
        tiaOrRindPct: defaultValues.get('CICoveredConditions.tiaOrRindPct'),
        benignBrainTumorPct: defaultValues.get('CICoveredConditions.benignBrainTumorPct'),
        comaPct: defaultValues.get('CICoveredConditions.comaPct'),
        coronaryArteryDiseaseAngioplastyPct: defaultValues.get(
          'CICoveredConditions.coronaryArteryDiseaseAngioplastyPct'
        ),
        arrhythmiaPacemakerPlacementPct: defaultValues.get('CICoveredConditions.arrhythmiaPacemakerPlacementPct'),
        boneMarrowOrStemCellDonationPct: defaultValues.get('CICoveredConditions.boneMarrowOrStemCellDonationPct'),
        pulmonaryEmbolismPct: defaultValues.get('CICoveredConditions.pulmonaryEmbolismPct'),
        aorticOrMitralValveRepairOrReplacementPct: defaultValues.get(
          'CICoveredConditions.aorticOrMitralValveRepairOrReplacementPct'
        ),
        heartAttackPct: defaultValues.get('CICoveredConditions.heartAttackPct'),
        idiopathicPulmonaryFibrosisPct: defaultValues.get('CICoveredConditions.idiopathicPulmonaryFibrosisPct'),
        paralysisPct: defaultValues.get('CICoveredConditions.paralysisPct'),
        advancedAlzheimersPct: defaultValues.get('CICoveredConditions.advancedAlzheimersPct'),
      },
    ].map(e => ({...e, ...createEisTypeObject('CICoveredConditions', rootId, parentId)})),
    recurrenceDetails: {
      maxNumberOfRecurrencesCd: defaultValues.get('CIRecurrenceBenefit.maxNumberOfRecurrencesCd'),
      separationPeriodCd: defaultValues.get('CIRecurrenceBenefit.separationPeriodCd'),
      ...createEisTypeObject('CIRecurrenceBenefit', rootId, parentId),
    },
    portabilityDetails: createEisTypeObject('CIPortabilityBenefit', rootId, parentId),
    offerStatus: 'offered',
    isHealthScreeningApplied: !['MI', 'ID'].includes(situsStateCd),
    isWaiverOfPremiumApplied: defaultValues.get('CIBenefitSchedule.isWaiverOfPremiumApplied'),
    isPortabilityApplied: defaultValues.get('CIBenefitSchedule.isPortabilityApplied') ?? true,
    isLifestyleEnhancementRiderApplied: false,
    isRecurrenceApplied: defaultValues.get('CIBenefitSchedule.isRecurrenceApplied'),
    isSecondEvaluationTransportationAndLodgingRiderApplied:
      defaultValues.get('CIBenefitSchedule.isSecondEvaluationTransportationAndLodgingRiderApplied') ?? false,
    isSkinCancerRiderApplied: true,
    isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied: !['CA', 'UT'].includes(situsStateCd),
    isSpecifiedChronicIllnessRiderApplied: !['CA'].includes(situsStateCd),
    specifiedChronicIllnessRider: {
      planBenefitAmountPct: defaultValues.get('CISpecifiedChronicIllnessRider.planBenefitAmountPct'),
      exclusions: [
        {
          ...createEisTypeObject('CIExclSCIBenefitIntentionallySelfInflictedInjury', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessRider.CIExclSCIBenefitIntentionallySelfInflictedInjury'
          ),
        },
        {
          ...createEisTypeObject('CIExclSCIBenefitAlcoholAbuse', rootId, parentId),
          isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitAlcoholAbuse'),
        },
        {
          ...createEisTypeObject('CIExclSCIBenefitDrugAbuse', rootId, parentId),
          isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDrugAbuse'),
        },
        {
          ...createEisTypeObject('CIExclSCIBenefitDiagnosedBeforeEffectiveDate', rootId, parentId),
          isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDiagnosedBeforeEffectiveDate'),
        },
        {
          ...createEisTypeObject('CIExclSCIBenefitDiagnosedOutsideOfTheUS', rootId, parentId),
          isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDiagnosedOutsideOfTheUS'),
        },
        {
          ...createEisTypeObject('CIExclSCIBenefitPreExConditions', rootId, parentId),
          isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitPreExConditions'),
        },
      ],
      ...createEisTypeObject('CISpecifiedChronicIllnessRider', rootId, parentId),
    },
    specifiedChronicIllnessAndAccidentalInjuryRider: {
      injuryPct: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.injuryPct'),
      planBenefitAmountPct: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.planBenefitAmountPct'),
      exclusions: [
        {
          ...createEisTypeObject('CIExclSCIandAIBenefitIntentionallySelfInflictedInjury', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitIntentionallySelfInflictedInjury'
          ),
        },
        {
          ...createEisTypeObject('CIExclSCIandAIBenefitAlcoholAbuse', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitAlcoholAbuse'
          ),
        },
        {
          ...createEisTypeObject('CIExclSCIandAIBenefitDrugAbuse', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDrugAbuse'
          ),
        },
        {
          ...createEisTypeObject('CIExclSCIandAIBenefitDiagnosedBeforeEffectiveDate', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDiagnosedBeforeEffectiveDate'
          ),
        },
        {
          ...createEisTypeObject('CIExclSCIandAIBenefitDiagnosedOutsideOfTheUS', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDiagnosedOutsideOfTheUS'
          ),
        },
        {
          ...createEisTypeObject('CIExclSCIandAIBenefitPreExConditions', rootId, parentId),
          isExclusion: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitPreExConditions'
          ),
        },
      ],
      ...createEisTypeObject('CISpecifiedChronicIllnessAndAccidentalInjuryRider', rootId, parentId),
    },
    isLifetimeMaximumApplied: defaultValues.get('CIBenefitSchedule.isLifetimeMaximumApplied'),
    skinCancerRider: {
      benefitAmount: {amount: +defaultValues.get('CISkinCancerRider.benefitAmount'), currency: 'USD'},
      benefitTypeCd: defaultValues.get('CISkinCancerRider.benefitTypeCd'),
      timePeriod: defaultValues.get('CISkinCancerRider.timePeriod') ?? 'CalendarYear',
      isSquamousCellCarcinomaOfTheSkin: defaultValues.get('CISkinCancerRider.isSquamousCellCarcinomaOfTheSkin'),
      isBasalCellCarcinoma: defaultValues.get('CISkinCancerRider.isBasalCellCarcinoma'),
      exclusions: [
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclIntentionallySelfInflictedInjury.isExclusion') ?? true,
          ...createEisTypeObject('CISkinCancerRiderExclIntentionallySelfInflictedInjury', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclAlcoholAbuse.isExclusion') ?? true,
          ...createEisTypeObject('CISkinCancerRiderExclAlcoholAbuse', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclDrugAbuse.isExclusion') ?? true,
          ...createEisTypeObject('CISkinCancerRiderExclDrugAbuse', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclDiagnosedBeforeEffectiveDate.isExclusion') ?? false,
          ...createEisTypeObject('CISkinCancerRiderExclDiagnosedBeforeEffectiveDate', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclDiagnosedOutsideOfUSCanada.isExclusion') ?? true,
          ...createEisTypeObject('CISkinCancerRiderExclDiagnosedOutsideOfUSCanada', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclMalignantMelanoma.isExclusion') ?? true,
          ...createEisTypeObject('CISkinCancerRiderExclMalignantMelanoma', rootId, parentId),
        },
        {
          isExclusion:
            defaultValues.get('CISkinCancerRiderExclAnyConditionsConsideredPreCancerous.isExclusion') ?? true,
          ...createEisTypeObject('CISkinCancerRiderExclAnyConditionsConsideredPreCancerous', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CISkinCancerRiderExclPreExConditions.isExclusion') ?? false,
          ...createEisTypeObject('CISkinCancerRiderExclPreExConditions', rootId, parentId),
        },
      ],
      ...createEisTypeObject('CISkinCancerRider', rootId, parentId),
    },
    specConditionAndInfDiseaseDetails: {
      // planBnfAmount?
      // exclusions: [
      //   'CIExclSpecConditionAndInfDiseaseIntentionallySelfInflictedInjury',
      //   'CIExclSpecConditionAndInfDiseaseAlcoholAbuse',
      //   'CIExclSpecConditionAndInfDiseaseDrugAbuse',
      //   'CIExclSpecConditionAndInfDiseaseDiagnosedBeforeEffectiveDate',
      //   'CIExclSpecConditionAndInfDiseaseDiagnosedOutsideOfTheUS',
      //   'CIExclSpecConditionAndInfDiseasePreExCondition'
      // ].map(exclusionType => ({
      //   isExclusion: defaultValues.get(`${exclusionType}.isExclusion`) ?? true,
      //   ...createEisTypeObject(exclusionType, rootId, parentId),
      // })),
      exclusions: [
        {
          isExclusion:
            defaultValues.get('CIExclSpecConditionAndInfDiseaseIntentionallySelfInflictedInjury.isExclusion') ?? true,
          ...createEisTypeObject('CIExclSpecConditionAndInfDiseaseIntentionallySelfInflictedInjury', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CIExclSpecConditionAndInfDiseaseAlcoholAbuse.isExclusion') ?? true,
          ...createEisTypeObject('CIExclSpecConditionAndInfDiseaseAlcoholAbuse', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CIExclSpecConditionAndInfDiseaseDrugAbuse.isExclusion') ?? true,
          ...createEisTypeObject('CIExclSpecConditionAndInfDiseaseDrugAbuse', rootId, parentId),
        },
        {
          isExclusion:
            defaultValues.get('CIExclSpecConditionAndInfDiseaseDiagnosedBeforeEffectiveDate.isExclusion') ?? false,
          ...createEisTypeObject('CIExclSpecConditionAndInfDiseaseDiagnosedBeforeEffectiveDate', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CIExclSpecConditionAndInfDiseaseDiagnosedOutsideOfTheUS.isExclusion') ?? true,
          ...createEisTypeObject('CIExclSpecConditionAndInfDiseaseDiagnosedOutsideOfTheUS', rootId, parentId),
        },
        {
          isExclusion: defaultValues.get('CIExclSpecConditionAndInfDiseasePreExCondition.isExclusion') ?? false,
          ...createEisTypeObject('CIExclSpecConditionAndInfDiseasePreExCondition', rootId, parentId),
        },
      ],
      ...createEisTypeObject('CISkinCancerRider', rootId, parentId),
      planBnfAmountPct: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.planBnfAmountPct'),
      isMalaria: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isMalaria'),
      isOsteomyelitis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isOsteomyelitis'),
      isTetanus: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isTetanus'),
      isNecrotizingFasciitis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isNecrotizingFasciitis'),
      isDiphtheria: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isDiphtheria'),
      isRabies: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isRabies'),
      // isAcuteRespiratoryDistressSyndrome is made true as per request
      isAcuteRespiratoryDistressSyndrome: true,
      isEncephalitis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isEncephalitis'),
      isMyastheniaGravis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isMyastheniaGravis'),
      isLegionnaireDisease: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isLegionnaireDisease'),
      isPoliomyelitis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isPoliomyelitis'),
      isCerebralPalsy: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isCerebralPalsy'),
      isMuscularDystrophy: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isMuscularDystrophy'),
      isCysticFibrosis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isCysticFibrosis'),
      isNovelInfectiousDisease: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isNovelInfectiousDisease'),
      isSystemicLupus: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isSystemicLupus'),
      isAdrenalInsufficiency: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isAdrenalInsufficiency'),
      isBacterialMeningitis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isBacterialMeningitis'),
      isHuntingtonChorea: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isHuntingtonChorea'),
      isSickleCellAnemia: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isSickleCellAnemia'),
      isTuberculosis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isTuberculosis'),
      isScleroderma: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isScleroderma'),
      isMultipleSclerosis: defaultValues.get('CISpecConditionAndInfDiseaseBenefit.isMultipleSclerosis'),
      isAmyotrophicLateralSclerosis: defaultValues.get(
        'CISpecConditionAndInfDiseaseBenefit.isAmyotrophicLateralSclerosis'
      ),
      ...createEisTypeObject('CISpecConditionAndInfDiseaseBenefit', rootId, parentId),
    },
    healthScreeningDetails: !['MI', 'ID'].includes(situsStateCd)
      ? createHealthScreeningDetails(defaultValues, rootId, parentId)
      : undefined,
    ...createEisTypeObject('CIBenefitSchedule', rootId, parentId),
    // should we map the hidden part.
  };
};

const createWhoCovered = (rootId: string, parentId: string) =>
  ['Individual', 'IndividualAndSpouse', 'IndividualAndChildren', 'Family'].map(tierCd => ({
    ...createEisTypeObject('CITier', rootId, parentId),
    tierCd: tierCd,
  }));

const createCIBenefitStructure = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return [
    {
      benefitTypeCd: defaultValues.get('CIBenefitStructureIndividual.benefitTypeCd'),
      benefitAmount: {
        amount: !Number.isNaN(+defaultValues.get('CIBenefitStructureIndividual.benefitAmount'))
          ? Number.parseInt(defaultValues.get('CIBenefitStructureIndividual.benefitAmount'))
          : 0,
        currency: 'USD',
      },
      maxBenefitAmount: {
        amount: 0,
        currency: 'USD',
      },
      minBenefitAmount: {
        amount: 0,
        currency: 'USD',
      },
      incrementAmount: {
        amount: 0,
        currency: 'USD',
      },
      ...createEisTypeObject('CIBenefitStructureIndividual', rootId, parentId),
    },
    {
      benefitTypeCd: defaultValues.get('CIBenefitStructureSpouse.benefitTypeCd'),
      individualAmtPct: defaultValues.get('CIBenefitStructureSpouse.individualAmtPct'),
      ...createEisTypeObject('CIBenefitStructureSpouse', rootId, parentId),
    },
    {
      benefitTypeCd: defaultValues.get('CIBenefitStructureChild.benefitTypeCd'),
      individualAmtPct: defaultValues.get('CIBenefitStructureChild.individualAmtPct'),
      ...createEisTypeObject('CIBenefitStructureChild', rootId, parentId),
    },
  ];
};

const createHealthScreeningDetails = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  const getAmount = (conditionType: string, conditionName: string) => {
    return {
      amount: Number.parseInt(defaultValues.get(`${conditionType}.${conditionName}`)),
      currency: 'USD',
    };
  };
  const getOptionsObject = () =>
    [
      'CiHsbOptionColonoscopy',
      'CiHsbOptionSerumProteinElectrophoresis',
      'CiHsbOptionFastingPlasmaGlucoseTest',
      'CiHsbOptionFastingBloodGlucoseTest',
      'CiHsbOptionTestingForDonationOfBoneMarrow',
      'CiHsbOptionCarcinoembryonicAntigenBloodTest',
      'CiHsbOptionHemoglobinA1C',
      'CiHsbOptionStressTestOnBikeorTreadmill',
      'CiHsbOptionLipidPanelCholesterolCount',
      'CiHsbOptionDopplerScreeningForCancer',
      'CiHsbOptionProstateDashSpecificAntigenTest',
      'CiHsbOptionBloodTestForTriglycerides',
      'CiHsbOptionClinicalTesticularExam',
      'CiHsbOptionFlexibleSigmoidoscopy',
      'CiHsbOptionSkinExam',
      'CiHsbOptionPapSmear',
      'CiHsbOptionDopplerScreeningForCarotids',
      'CiHsbOptionElectrocardiogram',
      'CiHsbOptionOtherBenefits',
      'CiHsbOptionChestXRay',
      'CiHsbOptionCompleteBloodCount',
      'CiHsbOptionBloodChemistryPanel',
      'CiHsbOptionTwoDashHourPostDashLoadPlasmaGlucoseTest',
      'CiHsbOptionNovelInfectiousImmunization',
      'CiHsbOptionEndoscopy',
      'CiHsbOptionUltrasoundForCancerDetection',
      'CiHsbOptionEchocardiogram',
      'CiHsbOptionBiopsyForCancer',
      'CiHsbOptionThermography',
      'CiHsbOptionElectroencephalogram',
      'CiHsbOptionBloodOrTissueTestingForGeneticSusceptibilityOfCancer',
      'CiHsbOptionBiopsyForSkinCancer',
      'CiHsbOptionHemoccultStoolAnalysis',
      'CiHsbOptionHumanPapillomavirusVaccination',
      'CiHsbOptionUltrasoundOfAbdominalAortaForAorticAneurysms',
      'CiHsbOptionNovelInfectiousDiseaseTesting',
      'CiHsbOptionCancerAntigen125BloodTest',
      'CiHsbOptionDopplerScreeningForPeripheralVascularDisease',
      'CiHsbOptionSkinCancerScreening',
      'CiHsbOptionMammography',
      'CiHsbOptionCancerAntigen15Dash3BloodTest',
      'CiHsbOptionOralCancerScreening',
    ].map(optionName => ({
      isOptionApplied: defaultValues.get(`${optionName}.isOptionApplied`),
      // assignedSubCategoryName: defaultValues.get(`${optionName}.assignedSubCategoryName`),
      // assignedCategoryName: defaultValues.get(`${optionName}.assignedCategoryName`),
      assignedCategoryName: 'CategoryFixed',
      assignedSubCategoryName: 'BASIC',
      ...createEisTypeObject(`${optionName}`, rootId, parentId),
    }));
  return {
    benefitAmount: getAmount('CiHealthScreeningBenefit', 'benefitAmount'),
    isPreventativeEnabled: defaultValues.get('CiHealthScreeningBenefit.isPreventativeEnabled'),
    maxBenefitNumber: defaultValues.get('CiHealthScreeningBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('CiHealthScreeningBenefit.timePeriodCd'),
    healthScreeningHashUnits: Number.parseInt(defaultValues.get('CiHealthScreeningBenefit.healthScreeningHashUnits')),
    healthScreeningType: defaultValues.get('CiHealthScreeningBenefit.healthScreeningType'),
    optionCategories: [
      {
        isApplied: defaultValues.get('CiHsbCategoryFixed.isApplied'),
        categoryName: defaultValues.get('CiHsbCategoryFixed.categoryName'),
        ...createEisTypeObject('CiHsbCategoryFixed', rootId, parentId),
      },
    ],
    options: getOptionsObject(),
    exclusions: [
      {
        isExclusion: defaultValues.get('CiHsbExclIntentionallySelfInflictedInjury.isExclusion') ?? true,
        ...createEisTypeObject('CiHsbExclIntentionallySelfInflictedInjury', rootId, parentId),
      },
      {
        isExclusion: defaultValues.get('CiHsbExclAlcoholAbuse.isExclusion') ?? true,
        ...createEisTypeObject('CiHsbExclAlcoholAbuse', rootId, parentId),
      },
      {
        isExclusion: defaultValues.get('CiHsbExclDrugAbuse.isExclusion') ?? true,
        ...createEisTypeObject('CiHsbExclDrugAbuse', rootId, parentId),
      },
      {
        isExclusion: defaultValues.get('CiHsbExclDiagnosedBeforeEffectiveDate.isExclusion') ?? false,
        ...createEisTypeObject('CiHsbExclDiagnosedBeforeEffectiveDate', rootId, parentId),
      },
      {
        isExclusion: defaultValues.get('CiHsbExclDiagnosedOutsideUsTerritoriesOrCanada.isExclusion') ?? true,
        ...createEisTypeObject('CiHsbExclDiagnosedOutsideUsTerritoriesOrCanada', rootId, parentId),
      },
      {
        isExclusion: defaultValues.get('CiHsbExclPreDashExistingConditions.isExclusion') ?? false,
        ...createEisTypeObject('CiHsbExclPreDashExistingConditions', rootId, parentId),
      },
    ],
    ...createEisTypeObject('CiHealthScreeningBenefit', rootId, parentId),
  };
};
